import React, { useEffect, useMemo, useRef, useState } from "react";

/**
 * Hybrid Chat Interface - Modern Chat with Start Activity Button
 * Based on ModernChatInterface with an initial "Get Started" pill button
 */
export default function HybridChatInterface({
  displayedText,
  initialMessage = "Message from System: If the user hasn't started any of the questions yet please greet them and immediately start the lesson, otherwise tell them welcome back and get back into the lesson",
  question,
  handleInputChange,
  handleKeyDown,
  handleSendMessage,
  isLoading,
  isTyping,
  handleSkipTyping,
  messagesContainerRef,
  conversationHistory = [],
  handleVoiceInput,
  isListening,
}) {
  const [localMessages, setLocalMessages] = useState([]);
  const [showStartScreen, setShowStartScreen] = useState(true);
  const [activityStarted, setActivityStarted] = useState(false);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const prevDisplayedTextRef = useRef("");
  const prevQuestionRef = useRef("");
  const messagesAreaRef = useRef(null);
  const [nearBottom, setNearBottom] = useState(true);

  // Voice input state and refs
  const listeningTimerRef = useRef(null);
  const listeningStartTimeRef = useRef(null);
  const lastTextChangeTimeRef = useRef(Date.now());
  const [autoVoiceSendEnabled, setAutoVoiceSendEnabled] = useState(false);
  const forceStopListeningRef = useRef(null);

  // Track whether the input was cleared to empty via Backspace
  const lastKeyRef = useRef(null);
  const emptiedByBackspaceRef = useRef(false);

  const uid = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

  // Determine if voice input should be allowed
  const shouldAllowVoiceInput = !isLoading && !isTyping && !showStartScreen;

  // Listen for question changes and update the lastTextChangeTime
  useEffect(() => {
    if (isListening) {
      lastTextChangeTimeRef.current = Date.now();
    }
  }, [question, isListening]);

  // This effect actively stops listening when conditions change
  useEffect(() => {
    if (isListening && !shouldAllowVoiceInput) {
      if (forceStopListeningRef.current) {
        forceStopListeningRef.current();
      }
    }
  }, [isListening, shouldAllowVoiceInput, isTyping, isLoading]);

  // Voice input auto-send functionality
  useEffect(() => {
    if (isListening && shouldAllowVoiceInput) {
      if (!listeningStartTimeRef.current) {
        listeningStartTimeRef.current = Date.now();
        lastTextChangeTimeRef.current = Date.now();
      }

      listeningTimerRef.current = setInterval(() => {
        const timeSinceLastTextChange =
          Date.now() - lastTextChangeTimeRef.current;

        if (
          timeSinceLastTextChange >= 4000 &&
          question &&
          question.trim() !== ""
        ) {
          setAutoVoiceSendEnabled(true);
          onSend();
          clearInterval(listeningTimerRef.current);
        }
      }, 300);
    } else {
      if (listeningTimerRef.current) {
        clearInterval(listeningTimerRef.current);
        listeningTimerRef.current = null;
      }
      listeningStartTimeRef.current = null;
    }

    // Auto-restart listening after typing completes if auto voice was enabled
    if (autoVoiceSendEnabled && shouldAllowVoiceInput && !isListening) {
      setAutoVoiceSendEnabled(false);
      setTimeout(() => {
        if (shouldAllowVoiceInput && handleVoiceInput) {
          handleVoiceInput();
        }
      }, 500);
    }

    return () => {
      if (listeningTimerRef.current) {
        clearInterval(listeningTimerRef.current);
      }
    };
  }, [
    isListening,
    question,
    isLoading,
    isTyping,
    autoVoiceSendEnabled,
    shouldAllowVoiceInput,
  ]);

  // Long inactivity timeout for mic (90 seconds)
  useEffect(() => {
    if (isListening) {
      const inactivityTimeout = setTimeout(() => {
        if (forceStopListeningRef.current) {
          forceStopListeningRef.current();
        }
      }, 90000);
      return () => clearTimeout(inactivityTimeout);
    }
  }, [question, isListening]);

  // Function to forcibly stop listening
  const forceStopVoiceInput = () => {
    if (isListening && handleVoiceInput) {
      handleVoiceInput();
    }
  };

  // Store the force stop function in the ref so effects can access it
  useEffect(() => {
    forceStopListeningRef.current = forceStopVoiceInput;
  }, [isListening]);

  // Custom voice input handler that only activates when allowed
  const handleSafeVoiceInput = () => {
    if (!handleVoiceInput) {
      console.error("handleVoiceInput prop is missing!");
      return;
    }

    if (shouldAllowVoiceInput) {
      handleVoiceInput();
    }
  };

  // Auto-skip typing
  useEffect(() => {
    if (isTyping && typeof handleSkipTyping === "function") {
      const id = requestAnimationFrame(() => {
        try {
          handleSkipTyping();
        } catch {}
      });
      return () => cancelAnimationFrame(id);
    }
  }, [isTyping, displayedText, handleSkipTyping]);

  // Track user messages
  useEffect(() => {
    const prev = prevQuestionRef.current;

    if (prev && prev !== question && !question) {
      if (!emptiedByBackspaceRef.current) {
        setLocalMessages((prevMsgs) => [
          ...prevMsgs,
          { id: uid(), role: "user", content: prev },
        ]);
      }
      emptiedByBackspaceRef.current = false;
    }

    prevQuestionRef.current = question || "";
  }, [question]);

  // Track assistant responses
  useEffect(() => {
    if (displayedText && displayedText !== prevDisplayedTextRef.current) {
      // Hide start screen when we get the first response
      if (showStartScreen) {
        setShowStartScreen(false);
      }

      setLocalMessages((prev) => {
        const filtered = prev.filter(
          (msg) => msg.role !== "assistant" || msg.content !== "..."
        );
        if (!prev.some((msg) => msg.content === displayedText)) {
          return [
            ...filtered,
            { id: uid(), role: "assistant", content: displayedText },
          ];
        }
        return filtered;
      });
    }
    prevDisplayedTextRef.current = displayedText || "";
  }, [displayedText, showStartScreen]);

  const allMessages = useMemo(
    () => [...(conversationHistory || []), ...localMessages],
    [conversationHistory, localMessages]
  );

  // Auto-scroll + near-bottom tracking on the scrollable area
  useEffect(() => {
    const el = messagesAreaRef.current;
    if (!el) return;

    const onScroll = () => {
      const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
      setNearBottom(distance < 80);
    };

    onScroll();
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!messagesEndRef.current || !nearBottom) return;
    messagesEndRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [allMessages, displayedText, nearBottom]);

  // Autofocus
  useEffect(() => {
    try {
      inputRef.current?.focus();
    } catch {}
  }, []);

  const onSend = (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (isLoading || !question?.trim()) return;

    const text = (question || "").trim();

    try {
      // FIX: send the message text (not the pooled event)
      handleSendMessage?.(text);
    } finally {
      handleInputChange?.({ target: { value: "" } });
      try {
        inputRef.current?.focus();
      } catch {}
    }
  };

  const handleStartActivity = () => {
    if (!activityStarted) {
      setActivityStarted(true);
      setShowStartScreen(false);

      // Send the initial message to start the activity
      setTimeout(() => {
        if (typeof handleSendMessage === "function") {
          handleSendMessage(initialMessage);
        }
      }, 100);
    }
  };

  // Wrap keydown to detect Backspace
  const onKeyDownWrapped = (e) => {
    lastKeyRef.current = e.key;
    handleKeyDown?.(e);
  };

  // Wrap onChange to set the "emptied by backspace" flag precisely
  const onChangeWrapped = (e) => {
    const next = e?.target?.value ?? "";
    const inputType = e?.nativeEvent?.inputType;
    const byBackspace =
      inputType === "deleteContentBackward" ||
      lastKeyRef.current === "Backspace";

    if (next === "" && byBackspace) {
      emptiedByBackspaceRef.current = true;
    } else if (next !== "") {
      emptiedByBackspaceRef.current = false;
    }

    handleInputChange?.(e);
  };

  const setMessagesAreaRef = (el) => {
    messagesAreaRef.current = el;
    if (typeof messagesContainerRef === "function") {
      messagesContainerRef(el);
    } else if (messagesContainerRef && "current" in messagesContainerRef) {
      messagesContainerRef.current = el;
    }
  };

  function TypingDots() {
    return (
      <div
        className="typing-bubble"
        role="status"
        aria-label="Assistant is typing"
      >
        <div className="typing-dots" aria-hidden="true">
          <span className="dot" />
          <span className="dot" />
          <span className="dot" />
        </div>
      </div>
    );
  }

  return (
    <div className="chat-container">
      <style>{styles}</style>

      <div className="chat-content" ref={setMessagesAreaRef}>
        {showStartScreen ? (
          <div className="welcome-screen">
            <div className="welcome-message">
              Hit the button below to get started with the activity
            </div>
            <button
              className="start-pill-button"
              onClick={handleStartActivity}
              aria-label="Get Started"
            >
              <span className="pill-label">Get Started</span>
            </button>
          </div>
        ) : (
          <div className="messages-list">
            {allMessages.map((msg, idx) => (
              <div key={msg.id || idx} className={`message ${msg.role}`}>
                {msg.role === "user" ? (
                  <div className="user-message">
                    <div className="message-content">
                      <div className="message-text">{msg.content}</div>
                    </div>
                  </div>
                ) : (
                  <div className="assistant-message">
                    <div className="message-content">
                      <div className="message-text">{msg.content}</div>
                    </div>
                  </div>
                )}
              </div>
            ))}

            {displayedText &&
              !allMessages.some((m) => m.content === displayedText) && (
                <div className="message assistant">
                  <div className="assistant-message">
                    <div className="message-content">
                      <div className="message-text">{displayedText}</div>
                    </div>
                  </div>
                </div>
              )}

            {isLoading && (
              <div className="message assistant">
                <div className="assistant-message">
                  <TypingDots />
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {!showStartScreen && (
        <div className="input-section">
          <form className="input-form" onSubmit={onSend}>
            <div className="input-wrapper figma-pill">
              {/* Mic button */}
              <button
                type="button"
                className={`mic-btn ${
                  isListening ? "is-listening" : "is-muted"
                } ${!shouldAllowVoiceInput ? "is-disabled" : ""}`}
                aria-label={
                  isListening ? "Stop listening" : "Start voice input"
                }
                aria-pressed={isListening}
                onClick={handleSafeVoiceInput}
                disabled={!shouldAllowVoiceInput}
              >
                {/* Default (muted) */}
                <span className="icon icon-muted" aria-hidden="true">
                  <svg
                    width="56"
                    height="56"
                    viewBox="0 0 56 56"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect
                      x="1"
                      y="1"
                      width="54"
                      height="54"
                      rx="27"
                      stroke="#D9D9DE"
                      strokeWidth="2"
                    />
                    <g clipPath="url(#clip0_muted)">
                      <path
                        d="M15.9219 14.8328C15.4812 14.3922 14.7687 14.3922 14.3328 14.8328C13.8969 15.2734 13.8922 15.9859 14.3281 16.4265L39.0781 41.1765C39.5188 41.6172 40.2313 41.6172 40.6672 41.1765C41.1031 40.7359 41.1078 40.0234 40.6672 39.5875L33.9078 32.8187C35.5109 31.1968 36.5 28.9656 36.5 26.5V24.625C36.5 24.0015 35.9984 23.5 35.375 23.5C34.7516 23.5 34.25 24.0015 34.25 24.625V26.5C34.25 28.3422 33.5141 30.0109 32.3141 31.2297L30.725 29.6406C31.5125 28.8297 32 27.7187 32 26.5V20.5C32 18.0156 29.9844 16 27.5 16C25.0156 16 23 18.0156 23 20.5V21.9156L15.9219 14.8375V14.8328ZM29.9047 35.1765L27.9641 33.2359C27.8094 33.2453 27.6594 33.25 27.5047 33.25C23.7781 33.25 20.7547 30.2265 20.7547 26.5V26.0219L18.7109 23.9781C18.5797 24.1609 18.5047 24.3859 18.5047 24.625V26.5C18.5047 31.089 21.9406 34.8765 26.3797 35.4297V37.75H24.1297C23.5062 37.75 23.0047 38.2515 23.0047 38.875C23.0047 39.4984 23.5062 40 24.1297 40H30.8797C31.5031 40 32.0047 39.4984 32.0047 38.875C32.0047 38.2515 31.5031 37.75 30.8797 37.75H28.6297V35.4297C29.0656 35.3734 29.4922 35.289 29.9094 35.1765H29.9047Z"
                        fill="#212121"
                      />
                    </g>
                    <defs>
                      <clipPath id="clip0_muted">
                        <rect
                          width="24"
                          height="24"
                          fill="white"
                          transform="translate(16 16)"
                        />
                      </clipPath>
                    </defs>
                  </svg>
                </span>

                {/* Selected (listening) */}
                <span className="icon icon-listening" aria-hidden="true">
                  <svg
                    width="56"
                    height="56"
                    viewBox="0 0 56 56"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect width="56" height="56" rx="28" fill="#212121" />
                    <path
                      d="M28 16C25.5156 16 23.5 18.0156 23.5 20.5V26.5C23.5 28.9844 25.5156 31 28 31C30.4844 31 32.5 28.9844 32.5 26.5V20.5C32.5 18.0156 30.4844 16 28 16ZM21.25 24.625C21.25 24.0016 20.7484 23.5 20.125 23.5C19.5016 23.5 19 24.0016 19 24.625V26.5C19 31.0891 22.4359 34.8766 26.875 35.4297V37.75H24.625C24.0016 37.75 23.5 38.2516 23.5 38.875C23.5 39.4984 24.0016 40 24.625 40H31.375C31.9984 40 32.5 39.4984 32.5 38.875C32.5 38.2516 31.9984 37.75 31.375 37.75H29.125V35.4297C33.5641 34.8766 37 31.0891 37 26.5V24.625C37 24.0016 36.4984 23.5 35.875 23.5C35.2516 23.5 34.75 24.0016 34.75 24.625V26.5C34.75 30.2266 31.7266 33.25 28 33.25C24.2734 33.25 21.25 30.2266 21.25 26.5V24.625Z"
                      fill="white"
                    />
                  </svg>
                </span>

                {/* Selected (hover preview to mute) */}
                <span className="icon icon-listening-hover" aria-hidden="true">
                  <svg
                    width="56"
                    height="56"
                    viewBox="0 0 56 56"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect width="56" height="56" rx="28" fill="#212121" />
                    <g clipPath="url(#clip0_hover)">
                      <path
                        d="M15.9219 14.8328C15.4812 14.3922 14.7687 14.3922 14.3328 14.8328C13.8969 15.2734 13.8922 15.9859 14.3281 16.4265L39.0781 41.1765C39.5188 41.6172 40.2313 41.6172 40.6672 41.1765C41.1031 40.7359 41.1078 40.0234 40.6672 39.5875L33.9078 32.8187C35.5109 31.1968 36.5 28.9656 36.5 26.5V24.625C36.5 24.0015 35.9984 23.5 35.375 23.5C34.7516 23.5 34.25 24.0015 34.25 24.625V26.5C34.25 28.3422 33.5141 30.0109 32.3141 31.2297L30.725 29.6406C31.5125 28.8297 32 27.7187 32 26.5V20.5C32 18.0156 29.9844 16 27.5 16C25.0156 16 23 18.0156 23 20.5V21.9156L15.9219 14.8375V14.8328ZM29.9047 35.1765L27.9641 33.2359C27.8094 33.2453 27.6594 33.25 27.5047 33.25C23.7781 33.25 20.7547 30.2265 20.7547 26.5V26.0219L18.7109 23.9781C18.5797 24.1609 18.5047 24.3859 18.5047 24.625V26.5C18.5047 31.089 21.9406 34.8765 26.3797 35.4297V37.75H24.1297C23.5062 37.75 23.0047 38.2515 23.0047 38.875C23.0047 39.4984 23.5062 40 24.1297 40H30.8797C31.5031 40 32.0047 39.4984 32.0047 38.875C32.0047 38.2515 31.5031 37.75 30.8797 37.75H28.6297V35.4297C29.0656 35.3734 29.4922 35.289 29.9094 35.1765H29.9047Z"
                        fill="white"
                      />
                    </g>
                    <defs>
                      <clipPath id="clip0_hover">
                        <rect
                          width="24"
                          height="24"
                          fill="white"
                          transform="translate(16 16)"
                        />
                      </clipPath>
                    </defs>
                  </svg>
                </span>
              </button>

              <input
                ref={inputRef}
                type="text"
                className="chat-input figma"
                value={question || ""}
                onChange={onChangeWrapped}
                onKeyDown={onKeyDownWrapped}
                placeholder="Ask me a question!"
                disabled={!!isLoading}
                autoComplete="off"
              />

              <button
                type="submit"
                className={`send-btn ${isLoading ? "is-loading" : ""}`}
                aria-label="Send"
                aria-busy={!!isLoading}
                disabled={!question?.trim() || !!isLoading}
              >
                <span className="icon default" aria-hidden="true">
                  <svg
                    width="56"
                    height="56"
                    viewBox="0 0 56 56"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect
                      x="1"
                      y="1"
                      width="54"
                      height="54"
                      rx="27"
                      stroke="#D9D9DE"
                      strokeWidth="2"
                    />
                    <path
                      d="M18.0515 16.1406L39.0382 26.4062C39.625 26.6922 40 27.3156 40 28C40 28.6844 39.625 29.3078 39.0382 29.5938L18.0515 39.8594C17.8618 39.9531 17.6544 40 17.4471 40C16.6485 40 16 39.3062 16 38.4531C16 38.2375 16.0441 38.0219 16.1235 37.825L19.3 30.2359C19.5074 29.7391 19.9574 29.3969 20.4691 29.35L27.3824 28.7125C27.7309 28.6797 27.9956 28.3703 27.9956 28C27.9956 27.6297 27.7265 27.3203 27.3824 27.2875L20.4691 26.65C19.9574 26.6031 19.5074 26.2656 19.3 25.7641L16.1235 18.175C16.0441 17.9781 16 17.7625 16 17.5469C16 16.6937 16.6485 16 17.4471 16C17.6544 16 17.8618 16.0469 18.0515 16.1406Z"
                      fill="#212121"
                    />
                  </svg>
                </span>

                <span className="icon hover" aria-hidden="true">
                  <svg
                    width="56"
                    height="56"
                    viewBox="0 0 56 56"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect width="56" height="56" rx="28" fill="#212121" />
                    <path
                      d="M18.0515 16.1406L39.0382 26.4062C39.625 26.6922 40 27.3156 40 28C40 28.6844 39.625 29.3078 39.0382 29.5938L18.0515 39.8594C17.8618 39.9531 17.6544 40 17.4471 40C16.6485 40 16 39.3062 16 38.4531C16 38.2375 16.0441 38.0219 16.1235 37.825L19.3 30.2359C19.5074 29.7391 19.9574 29.3969 20.4691 29.35L27.3824 28.7125C27.7309 28.6797 27.9956 28.3703 27.9956 28C27.9956 27.6297 27.7265 27.3203 27.3824 27.2875L20.4691 26.65C19.9574 26.6031 19.5074 26.2656 19.3 25.7641L16.1235 18.175C16.0441 17.9781 16 17.7625 16 17.5469C16 16.6937 16.6485 16 17.4471 16C17.6544 16 17.8618 16.0469 18.0515 16.1406Z"
                      fill="white"
                    />
                  </svg>
                </span>

                <span className="icon disabled" aria-hidden="true">
                  <svg
                    width="56"
                    height="56"
                    viewBox="0 0 56 56"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect
                      x="1"
                      y="1"
                      width="54"
                      height="54"
                      rx="27"
                      stroke="#D9D9DE"
                      strokeWidth="2"
                    />
                    <path
                      d="M18.0515 16.1406L39.0382 26.4062C39.625 26.6922 40 27.3156 40 28C40 28.6844 39.625 29.3078 39.0382 29.5938L18.0515 39.8594C17.8618 39.9531 17.6544 40 17.4471 40C16.6485 40 16 39.3062 16 38.4531C16 38.2375 16.0441 38.0219 16.1235 37.825L19.3 30.2359C19.5074 29.7391 19.9574 29.3969 20.4691 29.35L27.3824 28.7125C27.7309 28.6797 27.9956 28.3703 27.9956 28C27.9956 27.6297 27.7265 27.3203 27.3824 27.2875L20.4691 26.65C19.9574 26.6031 19.5074 26.2656 19.3 25.7641L16.1235 18.175C16.0441 17.9781 16 17.7625 16 17.5469C16 16.6937 16.6485 16 17.4471 16C17.6544 16 17.8618 16.0469 18.0515 16.1406Z"
                      fill="#D9D9DE"
                    />
                  </svg>
                </span>

                <span className="icon loading" aria-hidden="true">
                  <span className="loading-bg">
                    <span className="dot" />
                    <span className="dot" />
                    <span className="dot" />
                  </span>
                </span>
              </button>
            </div>
          </form>

          <div className="input-hint">
            AI responses are for general guidance only. Please verify before
            acting.
          </div>
        </div>
      )}
    </div>
  );
}

const styles = `
@import url("https://fonts.googleapis.com/css2?family=Inter:wght@400&display=swap");

* { box-sizing: border-box; margin: 0; padding: 0; }

.chat-container {
  width: 100vw; height: 100vh; max-width: none; margin: 0; background: #FFFFFF;
  border-radius: 0; box-shadow: none; display: flex; flex-direction: column;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  overflow: hidden;
}

.chat-content { flex: 1; overflow-y: auto; padding: 20px; background: #FFFFFF; }

.welcome-screen {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  height: 100%; text-align: center; padding: 40px; gap: 40px;
}

.welcome-message {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  font-style: normal; font-weight: 400; font-size: 32px; line-height: 100%; letter-spacing: 0; color: #374151;
}

/* Start Pill Button */
.start-pill-button {
  display: flex; flex-direction: row; justify-content: center; align-items: center;
  gap: 10px; width: 752px; max-width: 90%; height: 77px; padding: 24px;
  background: #ECECEE; border: 2px solid #D9D9DE; border-radius: 999px;
  cursor: pointer; transition: all 0.2s ease;
}

.start-pill-button:hover {
  background: #E5E5E7; transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.start-pill-button:active {
  transform: translateY(0); box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
}

.pill-label {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  font-style: normal; font-weight: 400; font-size: 24px; line-height: 29px;
  display: flex; align-items: center; text-align: center; color: #212121;
}

/* Messages */
.messages-list { display: flex; flex-direction: column; gap: 16px; max-width: 800px; margin: 0 auto; }

.message { animation: fadeIn 0.3s ease; display: flex; }
.message.assistant { justify-content: flex-start; padding: 8px 24px; }
.message.user { justify-content: flex-end; padding: 8px 24px; }

.assistant-message, .user-message {
  display: flex; align-items: center; padding: 16px 24px; gap: 10px; border-radius: 10px;
  width: fit-content; max-width: 100%; word-wrap: break-word; overflow-wrap: anywhere;
}

.assistant-message { max-width: 752px; background: transparent; }
.user-message { max-width: 640px; background: rgba(15, 97, 164, 0.2); }

.message-text {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  font-style: normal; font-weight: 400; font-size: 16px; line-height: 19px; display: flex; align-items: center; color: #212121;
}

/* Input Section */
.input-section { padding: 16px 20px 20px; background: #FFFFFF; border-top: 1px solid #E5E7EB; }
.input-form { width: 100%; }

.input-wrapper {
  display: flex; align-items: center; gap: 8px; padding: 8px 12px; background: #F9FAFB;
  border: 1px solid #E5E7EB; border-radius: 12px; transition: all 0.2s ease;
}
.input-wrapper:focus-within { background: #FFFFFF; border-color: #3B82F6; box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1); }

.chat-input { flex: 1; border: none; background: transparent; font-size: 15px; color: #111827; outline: none; padding: 4px; }
.chat-input::placeholder { color: #9CA3AF; }

/* Figma Input Pill */
.input-wrapper.figma-pill {
  display: flex; align-items: center; justify-content: space-between; gap: 24px; padding: 8px;
  width: 752px; height: 72px; max-width: 100%; margin: 0 auto; background: #ECECEE; border: 2px solid #D9D9DE; border-radius: 999px;
  box-sizing: border-box; transition: none;
}
.input-wrapper.figma-pill:focus-within { background: #ECECEE; border-color: #D9D9DE; box-shadow: none; }

/* Mic button */
.mic-btn {
  position: relative; width: 56px; height: 56px; padding: 0; border: none; background: transparent; border-radius: 999px;
  cursor: pointer; display: inline-flex; align-items: center; justify-content: center; transition: opacity 0.2s ease;
}

.mic-btn.is-disabled { cursor: not-allowed; opacity: 0.5; }

.mic-btn .icon { position: absolute; inset: 0; display: none; align-items: center; justify-content: center; line-height: 0; }
.mic-btn.is-muted .icon-muted { display: flex; }
.mic-btn.is-listening .icon-listening { display: flex; }
.mic-btn.is-listening:hover:not(.is-disabled) .icon-listening { display: none; }
.mic-btn.is-listening:hover:not(.is-disabled) .icon-listening-hover { display: flex; }

.mic-btn.is-listening:not(.is-disabled) { animation: micPulse 2s infinite ease-in-out; }
@keyframes micPulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.7; } }

/* Input field */
.chat-input.figma {
  flex: 1; min-width: 0; height: 56px; border: none; background: transparent; padding: 0 4px; outline: none;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  font-style: normal; font-weight: 400; font-size: 16px; line-height: 19px; color: #212121;
}
.chat-input.figma::placeholder { color: #525252; }

/* Send button */
.send-btn {
  position: relative; width: 56px; height: 56px; padding: 0; border: none; border-radius: 999px; background: transparent;
  cursor: pointer; display: inline-flex; align-items: center; justify-content: center; box-sizing: border-box; transition: transform 0.15s ease;
}
.send-btn:disabled { cursor: not-allowed; }

.send-btn .icon { position: absolute; inset: 0; display: none; align-items: center; justify-content: center; line-height: 0; }
.send-btn .icon.default { display: flex; }
.send-btn:hover:not(:disabled):not(.is-loading) .icon.default { display: none; }
.send-btn:hover:not(:disabled):not(.is-loading) .icon.hover { display: flex; }
.send-btn:disabled .icon { display: none; }
.send-btn:disabled .icon.disabled { display: flex; }

.send-btn.is-loading .icon { display: none; }
.send-btn.is-loading .icon.loading { display: flex; }

.send-btn .loading-bg { width: 56px; height: 56px; border-radius: 999px; background: #212121; display: inline-flex; align-items: center; justify-content: center; }
.send-btn .loading-bg .dot { width: 6px; height: 6px; border-radius: 50%; background: #FFFFFF; margin: 0 2px; animation: sendDots 1.1s infinite ease-in-out; }
.send-btn .loading-bg .dot:nth-child(2) { animation-delay: 0.15s; }
.send-btn .loading-bg .dot:nth-child(3) { animation-delay: 0.3s; }

@keyframes sendDots { 0%, 80%, 100% { opacity: 0.45; transform: translateY(0); } 40% { opacity: 1; transform: translateY(-2px); } }

.send-btn svg, .mic-btn svg { display: block; pointer-events: none; }

.input-hint { margin-top: 8px; font-size: 12px; color: #9CA3AF; text-align: center; }

/* Scrollbar */
.chat-content::-webkit-scrollbar { width: 6px; }
.chat-content::-webkit-scrollbar-track { background: transparent; }
.chat-content::-webkit-scrollbar-thumb { background: #E5E7EB; border-radius: 3px; }
.chat-content::-webkit-scrollbar-thumb:hover { background: #D1D5DB; }

/* Mobile Responsive */
@media (max-width: 640px) {
  .chat-container { width: 100vw; height: 100vh; border-radius: 0; }
  .user-message, .assistant-message { max-width: 90%; }
  .start-pill-button { width: 100%; max-width: 90%; }
}

/* Typing indicator */
.typing-bubble {
  display: flex; flex-direction: row; align-items: center; padding: 16px 24px; gap: 4px;
  width: 752px; height: 38px; max-width: 100%; border-radius: 10px;
}
.typing-dots { display: flex; flex-direction: row; align-items: center; gap: 4px; width: 26px; height: 6px; }
.typing-dots .dot { width: 6px; height: 6px; border-radius: 50%; background: #212121; animation: typingBounce 1.1s infinite ease-in-out; }
.typing-dots .dot:nth-child(2) { animation-delay: 0.15s; }
.typing-dots .dot:nth-child(3) { animation-delay: 0.30s; }
@keyframes typingBounce { 0%, 80%, 100% { transform: translateY(0); opacity: 0.55; } 40% { transform: translateY(-3px); opacity: 1; } }

@keyframes fadeIn { from { opacity: 0; transform: translateY(2px); } to { opacity: 1; transform: translateY(0); } }

/* Respect reduced motion */
@media (prefers-reduced-motion: reduce) {
  .typing-dots .dot { animation: none; opacity: 1; }
  .mic-btn.is-listening { animation: none; }
}

.assistant-message .typing-bubble { min-width: 180px; }
`;
